import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Users, User, UserCheck, AlertCircle, Heart } from 'lucide-react';
import { Family, Parent, Student } from '@/types/models';

interface FamilyCardProps {
  family: Family;
  parents: Parent[];
  students: Student[];
  onClick: () => void;
}

const FamilyCard: React.FC<FamilyCardProps> = ({ family, parents, students, onClick }) => {
  const getStatusInfo = () => {
    // Check if family is inactive first
    if (family.is_active === false) {
      return {
        label: 'Inactive',
        variant: 'secondary' as const,
        color: 'text-gray-600',
        bg: 'bg-gray-50',
        icon: AlertCircle
      };
    }
    
    const hasParents = parents.length > 0;
    const hasStudents = students.length > 0;
    
    if (hasParents && hasStudents) {
      return {
        label: 'Complete',
        variant: 'default' as const,
        color: 'text-green-600',
        bg: 'bg-green-50',
        icon: UserCheck
      };
    } else if (!hasParents && !hasStudents) {
      return {
        label: 'Setup Required',
        variant: 'destructive' as const,
        color: 'text-red-600',
        bg: 'bg-red-50',
        icon: AlertCircle
      };
    } else {
      return {
        label: 'Incomplete',
        variant: 'secondary' as const,
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        icon: AlertCircle
      };
    }
  };

  const status = getStatusInfo();
  const StatusIcon = status.icon;

  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || '?';
  };

  return (
    <motion.div
      whileHover={{ 
        scale: 1.02,
        transition: { duration: 0.2 }
      }}
      whileTap={{ scale: 0.98 }}
      className="h-full"
    >
      <Card 
        className={`h-full min-w-[340px] max-w-full p-6 transition-all duration-300 hover:shadow-lg hover:shadow-primary/10 hover:border-primary/30 group cursor-pointer ${
          family.is_active === false ? 'opacity-75 bg-gray-50/50 border-gray-300' : ''
        }`}
        onClick={onClick}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full flex items-center justify-center">
                <Users className="h-6 w-6 text-purple-500" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold group-hover:text-primary transition-colors duration-200 leading-tight">
                  {family.name}
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  {parents.length + students.length} members
                </p>
              </div>
            </div>
            
            <Badge variant={status.variant} className="shrink-0">
              <StatusIcon className="h-3 w-3 mr-1" />
              {status.label}
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Parents Section */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <User className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-medium text-muted-foreground">
                Parents ({parents.length})
              </span>
            </div>
            
            {parents.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {parents.slice(0, 3).map((parent) => (
                  <div key={parent.id} className="flex items-center gap-2 bg-blue-50 rounded-lg px-2 py-1 transition-all duration-200 hover:bg-blue-100 hover:scale-105 cursor-pointer">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="text-xs bg-blue-100 text-blue-700">
                        {getInitials(parent.first_name, parent.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-xs text-blue-700">
                      {parent.first_name} {parent.last_name}
                    </span>
                  </div>
                ))}
                {parents.length > 3 && (
                  <div className="flex items-center justify-center bg-blue-50 rounded-lg px-2 py-1 transition-all duration-200 hover:bg-blue-100 hover:scale-105 cursor-pointer">
                    <span className="text-xs text-blue-700">
                      +{parents.length - 3} more
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">
                No parents added yet
              </div>
            )}
          </div>

          {/* Students Section */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-4 w-4 text-purple-500" />
              <span className="text-sm font-medium text-muted-foreground">
                Students ({students.length})
              </span>
            </div>
            
            {students.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {students.slice(0, 4).map((student) => (
                  <div key={student.id} className="flex items-center gap-2 bg-purple-50 rounded-lg px-2 py-1 transition-all duration-200 hover:bg-purple-100 hover:scale-105 cursor-pointer">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="text-xs bg-purple-100 text-purple-700">
                        {getInitials(student.first_name, student.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-xs text-purple-700">
                      {student.first_name} {student.last_name}
                    </span>
                  </div>
                ))}
                {students.length > 4 && (
                  <div className="flex items-center justify-center bg-purple-50 rounded-lg px-2 py-1 transition-all duration-200 hover:bg-purple-100 hover:scale-105 cursor-pointer">
                    <span className="text-xs text-purple-700">
                      +{students.length - 4} more
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">
                No students added yet
              </div>
            )}
          </div>

          {/* Quick Stats */}
          <div className="pt-2 border-t border-border/50">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Family ID: {family.id.slice(-8)}</span>
              <span className="text-right">
                {family.created_at && new Date(family.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default FamilyCard;